import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { motion } from "framer-motion";
import ReactMarkdown from "react-markdown";

import { PrimVoicesProvider, usePrimVoices } from "primvoices-react";

import { API_URL } from "../config";
import { View, Text, Spinner, VoiceRunLogo } from "../components/base";
import { Icon } from "../components/base/Icon";

const DEFAULT_ACCENT = "#14b8a6";
const BAR_COUNT = 56;

// Deterministic pseudo-waveform — varied bar heights from the index, so every
// player looks like a real waveform without needing decoded audio data.
const BAR_HEIGHTS = Array.from({ length: BAR_COUNT }, (_, i) => {
  const v = Math.abs(Math.sin(i * 12.9898) * 43758.5453);
  return 0.28 + (v - Math.floor(v)) * 0.72;
});

type DemoMeta = {
  slug: string;
  title: string;
  subtitle: string | null;
  agentName: string | null;
  organizationName: string | null;
  accentColor: string | null;
  requiresPassword: boolean;
};

type TranscriptTurn = { role: string; content: string; timestamp?: unknown };

type DemoSession = {
  id: string;
  caption: string | null;
  origin: string | null;
  direction: string | null;
  status: string;
  createdAt: string;
  durationMs: number | null;
  turnCount: number;
  transcript: TranscriptTurn[];
  hasRecording: boolean;
};

type DemoHighlight = { label: string; value: string; sublabel?: string };

type DemoPayload = {
  token: string;
  slug: string;
  title: string;
  subtitle: string | null;
  intro: string | null;
  highlights: DemoHighlight[] | null;
  accentColor: string | null;
  ctas: { label: string; url: string }[] | null;
  webCallEnabled: boolean;
  phoneCallEnabled: boolean;
  agent: { name: string | null; description: string | null } | null;
  organizationName: string | null;
  sessions: DemoSession[];
};

/** Credentials-free fetch — the public page must never send a session cookie /
 *  org header, and must not trigger the app's 401 → sign-in redirect. */
const publicFetch = async (path: string, init?: RequestInit) => {
  const res = await fetch(`${API_URL}${path}`, {
    ...init,
    headers: { "Content-Type": "application/json", ...(init?.headers || {}) },
  });
  let json: any = null;
  try {
    json = await res.json();
  } catch {
    json = null;
  }
  return { ok: res.ok, status: res.status, json };
};

const recordingUrlFor = (slug: string, token: string, sessionId: string) =>
  `${API_URL}/v1/public/demo-shares/${slug}/recordings/${sessionId}?token=${encodeURIComponent(token)}`;

const fmtClock = (sec: number): string => {
  if (!isFinite(sec) || sec < 0) sec = 0;
  const m = Math.floor(sec / 60);
  const s = Math.floor(sec % 60);
  return `${m}:${String(s).padStart(2, "0")}`;
};

const fmtDuration = (ms: number | null): string | null => {
  if (!ms || ms <= 0) return null;
  const total = Math.round(ms / 1000);
  const m = Math.floor(total / 60);
  const s = total % 60;
  return m > 0 ? `${m}m ${s}s` : `${s}s`;
};

const fmtDate = (iso: string): string => {
  try {
    return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
  } catch {
    return "";
  }
};

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export const DemoSharePage = () => {
  const { slug } = useParams<{ slug: string }>();

  const [meta, setMeta] = useState<DemoMeta | null>(null);
  const [payload, setPayload] = useState<DemoPayload | null>(null);
  const [notFound, setNotFound] = useState(false);
  const [password, setPassword] = useState("");
  const [gateError, setGateError] = useState("");
  const [isUnlocking, setIsUnlocking] = useState(false);
  const [isLoadingMeta, setIsLoadingMeta] = useState(true);

  const accent = (payload?.accentColor || meta?.accentColor || DEFAULT_ACCENT) as string;

  const unlock = async (pw?: string) => {
    if (!slug) return;
    setIsUnlocking(true);
    setGateError("");
    const { ok, status, json } = await publicFetch(`/v1/public/demo-shares/${slug}/unlock`, {
      method: "POST",
      body: JSON.stringify({ password: pw ?? "" }),
    });
    setIsUnlocking(false);
    if (ok && json?.data) {
      setPayload(json.data as DemoPayload);
      return;
    }
    if (status === 401) setGateError("That password isn't right. Please try again.");
    else if (status === 429) setGateError("Too many attempts. Please try again in a little while.");
    else if (status === 404) setNotFound(true);
    else setGateError("Something went wrong. Please try again.");
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!slug) return;
      const { ok, json } = await publicFetch(`/v1/public/demo-shares/${slug}`);
      if (cancelled) return;
      setIsLoadingMeta(false);
      if (!ok || !json?.data) {
        setNotFound(true);
        return;
      }
      const m = json.data as DemoMeta;
      setMeta(m);
      document.title = `${m.title} · VoiceRun`;
      if (!m.requiresPassword) unlock();
    })();
    return () => {
      cancelled = true;
    };
  }, [slug]);

  if (notFound) {
    return (
      <Centered
        icon="error"
        title="Demo not found"
        subtitle="This link may have been disabled or has expired."
      />
    );
  }
  if (isLoadingMeta) {
    return (
      <View className="h-screen overflow-y-auto flex items-center justify-center bg-[#fafaf9] dark:bg-[#0a0b0d]">
        <Spinner size={28} />
      </View>
    );
  }
  if (!payload) {
    return (
      <Gate
        meta={meta}
        accent={accent}
        password={password}
        setPassword={setPassword}
        onSubmit={() => !isUnlocking && unlock(password)}
        error={gateError}
        isUnlocking={isUnlocking}
      />
    );
  }
  return (
    <DemoContent
      payload={payload}
      accent={accent}
    />
  );
};

// ---------------------------------------------------------------------------
// Gate
// ---------------------------------------------------------------------------

const Gate = ({
  meta,
  accent,
  password,
  setPassword,
  onSubmit,
  error,
  isUnlocking,
}: {
  meta: DemoMeta | null;
  accent: string;
  password: string;
  setPassword: (v: string) => void;
  onSubmit: () => void;
  error: string;
  isUnlocking: boolean;
}) => (
  <View
    className="h-screen overflow-y-auto flex items-center justify-center p-4 bg-[#fafaf9] dark:bg-[#0a0b0d]"
    style={{ backgroundImage: `radial-gradient(60rem 30rem at 50% -8rem, ${accent}1f, transparent)` }}
  >
    <motion.div
      initial={{ opacity: 0, y: 14 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.45 }}
      className="w-full max-w-sm"
    >
      <View className="rounded-2xl border border-gray-200/80 dark:border-white/10 bg-white/80 dark:bg-white/[0.03] backdrop-blur-xl shadow-[0_1px_0_rgba(255,255,255,0.6)_inset,0_20px_50px_-20px_rgba(0,0,0,0.25)] p-8">
        <View className="flex items-center justify-between mb-8">
          <VoiceRunLogo
            size="sm"
            variant="light"
          />
          <Text className="text-[10px] font-semibold uppercase tracking-[0.22em] text-gray-400 dark:text-gray-500">Confidential</Text>
        </View>

        <View className="flex flex-col items-center text-center">
          <View
            className="h-11 w-11 rounded-xl flex items-center justify-center mb-5 border"
            style={{ backgroundColor: `${accent}14`, borderColor: `${accent}33`, color: accent }}
          >
            <Icon
              name="lock"
              size={18}
            />
          </View>
          {meta?.organizationName && (
            <Text
              className="text-[10px] font-semibold uppercase tracking-[0.22em] mb-2 block"
              style={{ color: accent }}
            >
              {meta.organizationName}
            </Text>
          )}
          <Text className="text-xl font-bold text-gray-900 dark:text-white block leading-tight">{meta?.title}</Text>
          {meta?.subtitle && (
            <Text
              variant="muted"
              className="text-sm mt-1.5 block"
            >
              {meta.subtitle}
            </Text>
          )}
        </View>

        {meta?.requiresPassword ? (
          <form
            onSubmit={(e) => {
              e.preventDefault();
              onSubmit();
            }}
            className="mt-7 space-y-3"
          >
            <input
              type="password"
              autoFocus
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Access password"
              className="w-full rounded-lg border border-gray-200 dark:border-white/10 bg-white dark:bg-white/[0.04] px-4 py-2.5 text-center tracking-wider text-gray-900 dark:text-gray-100 placeholder:tracking-normal placeholder:text-gray-400 focus:outline-none focus:ring-2"
              style={{ ["--tw-ring-color" as any]: accent }}
            />
            {error && <Text className="text-xs text-red-600 dark:text-red-400 text-center block">{error}</Text>}
            <button
              type="submit"
              disabled={isUnlocking || !password}
              className="w-full rounded-lg px-4 py-2.5 text-sm font-semibold text-white transition-all hover:brightness-110 disabled:opacity-40 flex items-center justify-center gap-2"
              style={{ backgroundColor: accent }}
            >
              {isUnlocking ? (
                <Icon
                  name="loader"
                  size={15}
                  className="animate-spin"
                />
              ) : (
                <Icon
                  name="arrow-right"
                  size={15}
                />
              )}
              Enter demo
            </button>
          </form>
        ) : (
          <View className="flex justify-center mt-7">
            <Spinner size={22} />
          </View>
        )}
      </View>
      <Text className="text-center text-[10px] uppercase tracking-[0.2em] text-gray-400 dark:text-gray-600 mt-6 block">Confidential · Powered by VoiceRun</Text>
    </motion.div>
  </View>
);

// ---------------------------------------------------------------------------
// Unlocked content
// ---------------------------------------------------------------------------

const DemoContent = ({ payload, accent }: { payload: DemoPayload; accent: string }) => {
  const highlights = (payload.highlights || []).filter((h) => h.label && h.value);
  const agentName = payload.agent?.name || "";
  const sessions = payload.sessions || [];
  const totalMs = sessions.reduce((acc, s) => acc + (s.durationMs || 0), 0);

  // Derived stats round out the strip when the admin didn't author highlights.
  const derived: DemoHighlight[] = [
    { label: "Conversations", value: String(sessions.length) },
    ...(totalMs > 0 ? [{ label: "Total runtime", value: fmtDuration(totalMs) || "—" }] : []),
  ];
  const stats = highlights.length > 0 ? highlights : derived;

  // This app has no @tailwindcss/typography plugin, and Tailwind's preflight
  // strips default heading/list/paragraph styling — so map markdown elements
  // to explicit styles, otherwise the intro renders as undifferentiated text.
  const md = {
    p: ({ node, ...p }: any) => (
      <p
        className="mb-3 last:mb-0"
        {...p}
      />
    ),
    strong: ({ node, ...p }: any) => (
      <strong
        className="font-semibold text-gray-900 dark:text-white"
        {...p}
      />
    ),
    em: ({ node, ...p }: any) => (
      <em
        className="italic"
        {...p}
      />
    ),
    a: ({ node, ...p }: any) => (
      <a
        className="underline underline-offset-2"
        style={{ color: accent }}
        target="_blank"
        rel="noopener noreferrer"
        {...p}
      />
    ),
    ul: ({ node, ...p }: any) => (
      <ul
        className="list-disc pl-5 mb-3 space-y-1"
        {...p}
      />
    ),
    ol: ({ node, ...p }: any) => (
      <ol
        className="list-decimal pl-5 mb-3 space-y-1"
        {...p}
      />
    ),
    li: ({ node, ...p }: any) => (
      <li
        className="leading-relaxed"
        {...p}
      />
    ),
    h1: ({ node, ...p }: any) => (
      <h1
        className="text-xl font-bold text-gray-900 dark:text-white mt-5 mb-2 first:mt-0"
        {...p}
      />
    ),
    h2: ({ node, ...p }: any) => (
      <h2
        className="text-lg font-bold text-gray-900 dark:text-white mt-5 mb-2 first:mt-0"
        {...p}
      />
    ),
    h3: ({ node, ...p }: any) => (
      <h3
        className="text-base font-semibold text-gray-900 dark:text-white mt-4 mb-1.5 first:mt-0"
        {...p}
      />
    ),
    blockquote: ({ node, ...p }: any) => (
      <blockquote
        className="border-l-2 pl-3 my-3 italic text-gray-500 dark:text-gray-400"
        style={{ borderColor: accent }}
        {...p}
      />
    ),
    code: ({ node, ...p }: any) => (
      <code
        className="font-mono text-[13px] bg-gray-100 dark:bg-white/10 px-1.5 py-0.5 rounded"
        {...p}
      />
    ),
    hr: () => <hr className="my-4 border-gray-200 dark:border-white/10" />,
  };

  return (
    <View className="h-screen overflow-y-auto bg-[#fafaf9] dark:bg-[#0a0b0d] text-gray-900 dark:text-gray-100">
      {/* Sticky top rail */}
      <View className="sticky top-0 z-20 border-b border-gray-200/70 dark:border-white/10 bg-[#fafaf9]/80 dark:bg-[#0a0b0d]/80 backdrop-blur-xl">
        <View className="max-w-5xl mx-auto px-5 sm:px-8 h-12 flex items-center justify-between">
          <VoiceRunLogo
            size="sm"
            variant="light"
          />
          <Text className="text-[10px] font-semibold uppercase tracking-[0.22em] text-gray-400 dark:text-gray-500 shrink-0">Confidential demo</Text>
        </View>
      </View>

      {/* Hero */}
      <View
        className="relative border-b border-gray-200/70 dark:border-white/10"
        style={{ backgroundImage: `radial-gradient(50rem 22rem at 12% -6rem, ${accent}1c, transparent)` }}
      >
        <View className="max-w-5xl mx-auto px-5 sm:px-8 pt-12 pb-8">
          <motion.div
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            {payload.organizationName && (
              <Text
                className="text-[11px] font-semibold uppercase tracking-[0.22em] mb-3 block"
                style={{ color: accent }}
              >
                {payload.organizationName} · Voice AI demo
              </Text>
            )}
            <Text className="text-[2.1rem] sm:text-5xl font-bold tracking-tight leading-[1.05] text-gray-900 dark:text-white block max-w-3xl">
              {payload.title}
            </Text>
            {payload.subtitle && (
              <Text
                variant="muted"
                className="text-base sm:text-lg mt-3 block max-w-2xl"
              >
                {payload.subtitle}
              </Text>
            )}
            {agentName && (
              <View className="inline-flex items-center gap-2 mt-5 pl-2.5 pr-3 py-1 rounded-full border border-gray-200 dark:border-white/10 bg-white/70 dark:bg-white/[0.04]">
                <span style={{ color: accent }}>
                  <Icon
                    name="bot"
                    size={13}
                  />
                </span>
                <Text className="text-xs font-medium">{agentName}</Text>
                {payload.agent?.description && (
                  <Text
                    variant="muted"
                    className="text-xs hidden sm:inline border-l border-gray-200 dark:border-white/10 pl-2"
                  >
                    {payload.agent.description}
                  </Text>
                )}
              </View>
            )}
          </motion.div>
        </View>

        {/* Stats strip — tabular, hairline-separated */}
        {stats.length > 0 && (
          <View className="max-w-5xl mx-auto px-5 sm:px-8">
            <View className="grid grid-cols-2 sm:grid-cols-4 border-t border-gray-200/70 dark:border-white/10 divide-x divide-gray-200/70 dark:divide-white/10">
              {stats.slice(0, 4).map((h, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.15 + i * 0.06 }}
                  className="px-4 py-4 first:pl-0"
                >
                  <Text
                    className="text-2xl font-bold tabular-nums block leading-none"
                    style={{ color: accent }}
                  >
                    {h.value}
                  </Text>
                  <Text className="text-[11px] uppercase tracking-wider text-gray-500 dark:text-gray-400 mt-1.5 block">{h.label}</Text>
                  {h.sublabel && (
                    <Text
                      variant="muted"
                      className="text-[10px] mt-0.5 block"
                    >
                      {h.sublabel}
                    </Text>
                  )}
                </motion.div>
              ))}
            </View>
          </View>
        )}
      </View>

      <View className="max-w-5xl mx-auto px-5 sm:px-8 py-10 space-y-10">
        {/* Intro */}
        {payload.intro && (
          <View className="max-w-2xl text-[15px] leading-relaxed text-gray-600 dark:text-gray-300">
            <ReactMarkdown components={md}>{payload.intro}</ReactMarkdown>
          </View>
        )}

        {/* Try it live — talk in browser / get a phone call */}
        {(payload.webCallEnabled || payload.phoneCallEnabled) && (
          <LiveCallSection
            payload={payload}
            accent={accent}
          />
        )}

        {/* Conversations */}
        {sessions.length > 0 && (
          <View className="space-y-3">
            <View className="flex items-baseline justify-between">
              <Text className="text-[11px] font-semibold uppercase tracking-[0.22em] text-gray-500 dark:text-gray-400">Recorded conversations</Text>
              <Text
                variant="muted"
                className="text-[11px] tabular-nums"
              >
                {sessions.length} {sessions.length === 1 ? "call" : "calls"}
              </Text>
            </View>
            <View className="grid grid-cols-1 gap-3">
              {sessions.map((s, i) => (
                <ConversationCard
                  key={s.id}
                  session={s}
                  index={i}
                  accent={accent}
                  slug={payload.slug}
                  token={payload.token}
                />
              ))}
            </View>
          </View>
        )}

        {/* CTAs */}
        {payload.ctas && payload.ctas.length > 0 && (
          <View className="flex flex-col sm:flex-row flex-wrap items-center justify-center gap-3 pt-6 border-t border-gray-200/70 dark:border-white/10">
            {payload.ctas.map((cta, i) => (
              <a
                key={i}
                href={cta.url}
                target="_blank"
                rel="noopener noreferrer"
                className={
                  i === 0
                    ? "inline-flex items-center gap-2 rounded-lg px-6 py-3 text-sm font-semibold text-white transition-all hover:brightness-110"
                    : "inline-flex items-center gap-2 rounded-lg px-6 py-3 text-sm font-semibold border transition-colors hover:bg-gray-50 dark:hover:bg-white/[0.04]"
                }
                style={i === 0 ? { backgroundColor: accent } : { color: accent, borderColor: `${accent}66` }}
              >
                {cta.label}
                <Icon
                  name="arrow-right"
                  size={15}
                />
              </a>
            ))}
          </View>
        )}
      </View>

      <View className="border-t border-gray-200/70 dark:border-white/10 py-8 flex flex-col items-center gap-2">
        <VoiceRunLogo
          size="sm"
          variant="light"
        />
        <Text className="text-[10px] uppercase tracking-[0.2em] text-gray-400 dark:text-gray-600">Confidential demo</Text>
      </View>
    </View>
  );
};

// ---------------------------------------------------------------------------
// Conversation card
// ---------------------------------------------------------------------------

const ConversationCard = ({ session, index, accent, slug, token }: { session: DemoSession; index: number; accent: string; slug: string; token: string }) => {
  const [showTranscript, setShowTranscript] = useState(false);
  const title = session.caption || `Conversation ${String(index + 1).padStart(2, "0")}`;
  const audioUrl = session.hasRecording ? recordingUrlFor(slug, token, session.id) : null;

  const meta: { label: string; value: string }[] = [
    ...(fmtDuration(session.durationMs) ? [{ label: "Length", value: fmtDuration(session.durationMs) as string }] : []),
    { label: "Turns", value: String(session.turnCount) },
    ...(session.origin ? [{ label: "Source", value: session.origin }] : []),
    { label: "Date", value: fmtDate(session.createdAt) },
  ];

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-40px" }}
      transition={{ duration: 0.35 }}
      className="group rounded-xl border border-gray-200/80 dark:border-white/10 bg-white dark:bg-white/[0.02] overflow-hidden transition-colors hover:border-gray-300 dark:hover:border-white/20"
    >
      <View className="p-4 sm:p-5">
        <View className="flex items-start gap-3 mb-4">
          <Text className="text-xl font-bold tabular-nums text-gray-300 dark:text-gray-600 leading-none mt-0.5 select-none">
            {String(index + 1).padStart(2, "0")}
          </Text>
          <View className="min-w-0 flex-1">
            <Text className="text-[15px] font-semibold text-gray-900 dark:text-white block truncate">{title}</Text>
            {/* Dense metadata row */}
            <View className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-1.5">
              {meta.map((m) => (
                <View
                  key={m.label}
                  className="flex items-center gap-1.5"
                >
                  <Text className="text-[10px] uppercase tracking-wider text-gray-400 dark:text-gray-500">{m.label}</Text>
                  <Text className="text-xs font-medium tabular-nums text-gray-700 dark:text-gray-300">{m.value}</Text>
                </View>
              ))}
            </View>
          </View>
        </View>

        {audioUrl ? (
          <DemoAudioPlayer
            audioUrl={audioUrl}
            durationMs={session.durationMs}
            accent={accent}
            title={title}
          />
        ) : (
          <View className="flex items-center gap-2 rounded-lg border border-dashed border-gray-200 dark:border-white/10 px-3 py-3">
            <Icon
              name="mic-off"
              size={14}
              className="text-gray-400"
            />
            <Text
              variant="muted"
              className="text-xs"
            >
              Recording not available for this conversation.
            </Text>
          </View>
        )}

        {session.transcript.length > 0 && (
          <button
            onClick={() => setShowTranscript((v) => !v)}
            className="mt-3 inline-flex items-center gap-1.5 text-[11px] font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors"
          >
            <Icon
              name={showTranscript ? "chevron-up" : "chevron-down"}
              size={13}
            />
            {showTranscript ? "Hide transcript" : `Transcript · ${session.transcript.length} turns`}
          </button>
        )}
      </View>

      {showTranscript && session.transcript.length > 0 && (
        <View className="border-t border-gray-100 dark:border-white/[0.06] bg-gray-50/60 dark:bg-black/20 px-4 sm:px-5 py-4">
          <View className="space-y-2.5 max-h-96 overflow-y-auto pr-1">
            {session.transcript.map((turn, ti) => {
              const isUser = (turn.role || "").toLowerCase() === "user";
              return (
                <View
                  key={ti}
                  className={`flex flex-col ${isUser ? "items-end" : "items-start"}`}
                >
                  <Text className="text-[9px] uppercase tracking-[0.18em] text-gray-400 dark:text-gray-500 mb-0.5 px-1">{isUser ? "Caller" : "Agent"}</Text>
                  <View
                    className={`max-w-[84%] rounded-2xl px-3.5 py-2 text-[13px] leading-snug ${
                      isUser
                        ? "bg-white dark:bg-white/[0.06] border border-gray-200 dark:border-white/10 text-gray-800 dark:text-gray-100 rounded-tr-sm"
                        : "text-white rounded-tl-sm"
                    }`}
                    style={isUser ? undefined : { backgroundColor: accent }}
                  >
                    {turn.content}
                  </View>
                </View>
              );
            })}
          </View>
        </View>
      )}
    </motion.div>
  );
};

// ---------------------------------------------------------------------------
// Custom audio player (lazy-loaded, waveform scrubber)
// ---------------------------------------------------------------------------

const SPEEDS = [1, 1.25, 1.5, 2];

const DemoAudioPlayer = ({ audioUrl, durationMs, accent, title }: { audioUrl: string; durationMs: number | null; accent: string; title: string }) => {
  const ref = useRef<HTMLAudioElement | null>(null);
  const [playing, setPlaying] = useState(false);
  const [cur, setCur] = useState(0);
  const [dur, setDur] = useState(durationMs ? durationMs / 1000 : 0);
  const [speed, setSpeed] = useState(1);
  const [error, setError] = useState(false);

  // Lazily create the <audio> on first interaction so opening the page doesn't
  // fire a network request for every recording at once.
  const ensureAudio = () => {
    if (ref.current) return ref.current;
    const audio = new Audio(audioUrl);
    audio.playbackRate = speed;
    audio.addEventListener("timeupdate", () => setCur(audio.currentTime));
    audio.addEventListener("loadedmetadata", () => {
      if (isFinite(audio.duration) && audio.duration > 0) setDur(audio.duration);
    });
    audio.addEventListener("ended", () => setPlaying(false));
    audio.addEventListener("error", () => {
      setError(true);
      setPlaying(false);
    });
    ref.current = audio;
    return audio;
  };

  useEffect(() => {
    return () => {
      ref.current?.pause();
      ref.current = null;
    };
  }, []);

  const toggle = () => {
    const a = ensureAudio();
    if (playing) {
      a.pause();
      setPlaying(false);
    } else {
      a.play()
        .then(() => setPlaying(true))
        .catch(() => setError(true));
    }
  };

  const seekTo = (clientX: number, rect: DOMRect) => {
    const a = ensureAudio();
    if (!dur) return;
    const pct = Math.min(1, Math.max(0, (clientX - rect.left) / rect.width));
    a.currentTime = pct * dur;
    setCur(pct * dur);
  };

  const cycleSpeed = () => {
    const next = SPEEDS[(SPEEDS.indexOf(speed) + 1) % SPEEDS.length];
    setSpeed(next);
    if (ref.current) ref.current.playbackRate = next;
  };

  const download = async () => {
    try {
      const res = await fetch(audioUrl);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${title.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}.wav`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      /* ignore */
    }
  };

  const progress = dur > 0 ? cur / dur : 0;

  if (error) {
    return (
      <View className="flex items-center gap-2 rounded-lg border border-gray-200 dark:border-white/10 px-3 py-3">
        <Icon
          name="warning-amber"
          size={14}
          className="text-gray-400"
        />
        <Text
          variant="muted"
          className="text-xs"
        >
          Couldn’t load this recording.
        </Text>
      </View>
    );
  }

  return (
    <View className="flex items-center gap-3 rounded-lg border border-gray-200 dark:border-white/10 bg-gray-50/70 dark:bg-white/[0.03] px-3 py-2.5">
      <button
        onClick={toggle}
        className="h-9 w-9 shrink-0 rounded-full flex items-center justify-center text-white transition-transform active:scale-95 hover:brightness-110"
        style={{ backgroundColor: accent }}
        aria-label={playing ? "Pause" : "Play"}
      >
        <Icon
          name={playing ? "pause" : "play"}
          size={18}
        />
      </button>

      {/* Waveform scrubber */}
      <div
        className="relative flex-1 h-9 flex items-center gap-[2px] cursor-pointer"
        onClick={(e) => seekTo(e.clientX, e.currentTarget.getBoundingClientRect())}
      >
        {BAR_HEIGHTS.map((h, i) => {
          const filled = i / BAR_COUNT <= progress;
          return (
            <div
              key={i}
              className="flex-1 rounded-full bg-gray-300 dark:bg-white/15 transition-colors"
              style={{
                height: `${Math.round(h * 100)}%`,
                ...(filled ? { backgroundColor: accent } : {}),
              }}
            />
          );
        })}
      </div>

      <Text className="text-[11px] tabular-nums text-gray-500 dark:text-gray-400 shrink-0 w-[68px] text-right">
        {fmtClock(cur)} / {fmtClock(dur)}
      </Text>

      <button
        onClick={cycleSpeed}
        className="shrink-0 text-[11px] font-semibold tabular-nums px-1.5 py-1 rounded text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors"
        aria-label="Playback speed"
      >
        {speed}×
      </button>

      <button
        onClick={download}
        className="shrink-0 h-7 w-7 rounded flex items-center justify-center text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors"
        aria-label="Download recording"
      >
        <Icon
          name="download"
          size={15}
        />
      </button>
    </View>
  );
};

// ---------------------------------------------------------------------------
// Live call (talk in browser / get a phone call)
// ---------------------------------------------------------------------------

const LiveCallSection = ({ payload, accent }: { payload: DemoPayload; accent: string }) => {
  const agentName = payload.agent?.name || "the agent";
  return (
    <View className="rounded-2xl border border-gray-200/80 dark:border-white/10 bg-white/70 dark:bg-white/[0.02] p-5 sm:p-6">
      <View className="flex items-center gap-2 mb-1">
        <span style={{ color: accent }}>
          <Icon
            name="zap"
            size={14}
          />
        </span>
        <Text
          className="text-[11px] font-semibold uppercase tracking-[0.22em]"
          style={{ color: accent }}
        >
          Try it live
        </Text>
      </View>
      <Text className="text-lg font-bold text-gray-900 dark:text-white block">Test {agentName} yourself</Text>
      <Text
        variant="muted"
        className="text-sm block mt-1"
      >
        Talk to the same agent featured in this demo — right here in your browser, or have it call your phone.
      </Text>
      <View className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-4">
        {payload.webCallEnabled && (
          <WebCallWidget
            slug={payload.slug}
            token={payload.token}
            accent={accent}
          />
        )}
        {payload.phoneCallEnabled && (
          <PhoneCallWidget
            slug={payload.slug}
            token={payload.token}
            accent={accent}
          />
        )}
      </View>
    </View>
  );
};

const WebCallWidget = ({ slug, token, accent }: { slug: string; token: string; accent: string }) => {
  const [attempt, setAttempt] = useState<{ key: number; config: any } | null>(null);
  const [starting, setStarting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const start = async () => {
    setStarting(true);
    setError(null);
    const { ok, status, json } = await publicFetch(`/v1/public/demo-shares/${slug}/web-call?token=${encodeURIComponent(token)}`, {
      method: "POST",
      body: JSON.stringify({}),
    });
    setStarting(false);
    if (!ok || !json?.data?.url) {
      setError(status === 429 ? "Too many attempts — try again in a bit." : json?.error || "Couldn't start the call. Please try again.");
      return;
    }
    const params = json.data.parameters || {};
    setAttempt((prev) => ({
      key: (prev?.key ?? 0) + 1,
      config: {
        apiUrl: API_URL,
        agentId: params.agentId || "demo",
        environment: params.environment,
        serverUrl: json.data.url,
        customParameters: params,
        logLevel: "WARN",
        origin: "web",
      },
    }));
  };

  if (attempt) {
    return (
      <PrimVoicesProvider
        key={attempt.key}
        config={attempt.config}
        autoConnect
      >
        <WebCallActive
          accent={accent}
          onEnd={() => setAttempt(null)}
        />
      </PrimVoicesProvider>
    );
  }

  return (
    <View className="rounded-xl border border-gray-200 dark:border-white/10 p-4 flex flex-col">
      <View className="flex items-center gap-2 mb-2">
        <span style={{ color: accent }}>
          <Icon
            name="mic"
            size={16}
          />
        </span>
        <Text className="text-sm font-semibold text-gray-900 dark:text-white">Talk in your browser</Text>
      </View>
      <Text
        variant="muted"
        className="text-xs flex-1 mb-3"
      >
        Use your microphone for a live, real-time conversation with the agent.
      </Text>
      <button
        onClick={start}
        disabled={starting}
        className="w-full rounded-lg px-4 py-2.5 text-sm font-semibold text-white transition-all hover:brightness-110 disabled:opacity-50 flex items-center justify-center gap-2"
        style={{ backgroundColor: accent }}
      >
        {starting ? (
          <Icon
            name="loader"
            size={15}
            className="animate-spin"
          />
        ) : (
          <Icon
            name="mic"
            size={15}
          />
        )}
        {starting ? "Connecting…" : "Talk to the agent"}
      </button>
      {error && <Text className="text-xs text-red-600 dark:text-red-400 mt-2">{error}</Text>}
    </View>
  );
};

const WebCallActive = ({ accent, onEnd }: { accent: string; onEnd: () => void }) => {
  const { startListening, stopListening, disconnect, isConnected, isListening, isPlaying, audioStats, error } = usePrimVoices();
  const micTried = useRef(false);

  // Auto-start the mic once connected (the user already gestured by clicking "Talk").
  useEffect(() => {
    if (isConnected && !micTried.current) {
      micTried.current = true;
      startListening().catch(() => undefined);
    }
    // eslint-disable-next-line
  }, [isConnected]);

  const end = () => {
    try {
      stopListening();
    } catch {
      /* noop */
    }
    try {
      disconnect();
    } catch {
      /* noop */
    }
    onEnd();
  };

  const level = audioStats?.level ?? 0;
  const status = !isConnected ? "Connecting…" : isPlaying ? "Agent speaking" : isListening ? "Listening" : "Connected";

  return (
    <View
      className="rounded-xl border p-4 flex flex-col"
      style={{ borderColor: `${accent}66` }}
    >
      <View className="flex items-center gap-2 mb-3">
        <View
          className="h-2.5 w-2.5 rounded-full animate-pulse"
          style={{ backgroundColor: accent }}
        />
        <Text className="text-sm font-semibold text-gray-900 dark:text-white">{status}</Text>
      </View>
      <View className="h-1.5 rounded-full bg-gray-200 dark:bg-white/10 overflow-hidden mb-3">
        <View
          className="h-full rounded-full transition-[width] duration-75"
          style={{ width: `${Math.min(100, Math.round(level * 100))}%`, backgroundColor: accent }}
        />
      </View>
      <View className="flex items-center gap-2 mt-auto">
        <button
          onClick={() => (isListening ? stopListening() : startListening().catch(() => undefined))}
          className="flex-1 rounded-lg px-3 py-2 text-sm font-medium border transition-colors flex items-center justify-center gap-1.5"
          style={{ borderColor: `${accent}66`, color: accent }}
        >
          <Icon
            name={isListening ? "mic" : "mic-off"}
            size={15}
          />
          {isListening ? "Mute" : "Unmute"}
        </button>
        <button
          onClick={end}
          className="flex-1 rounded-lg px-3 py-2 text-sm font-medium text-white bg-red-500 hover:bg-red-600 transition-colors flex items-center justify-center gap-1.5"
        >
          <Icon
            name="close"
            size={15}
          />
          End call
        </button>
      </View>
      {error && <Text className="text-xs text-red-600 dark:text-red-400 mt-2">{error}</Text>}
    </View>
  );
};

const PhoneCallWidget = ({ slug, token, accent }: { slug: string; token: string; accent: string }) => {
  const [phone, setPhone] = useState("");
  const [dialing, setDialing] = useState(false);
  const [state, setState] = useState<"idle" | "ok" | "error">("idle");
  const [error, setError] = useState("");

  const dial = async () => {
    if (!phone.trim() || dialing) return;
    setDialing(true);
    setState("idle");
    setError("");
    const { ok, status, json } = await publicFetch(`/v1/public/demo-shares/${slug}/call-phone?token=${encodeURIComponent(token)}`, {
      method: "POST",
      body: JSON.stringify({ phoneNumber: phone.trim() }),
    });
    setDialing(false);
    if (ok && json?.data?.ok) {
      setState("ok");
    } else {
      setState("error");
      setError(json?.error || (status === 429 ? "Too many call requests — try again later." : "Couldn't place the call. Please try again."));
    }
  };

  return (
    <View className="rounded-xl border border-gray-200 dark:border-white/10 p-4 flex flex-col">
      <View className="flex items-center gap-2 mb-2">
        <span style={{ color: accent }}>
          <Icon
            name="phone"
            size={16}
          />
        </span>
        <Text className="text-sm font-semibold text-gray-900 dark:text-white">Get a call</Text>
      </View>
      <Text
        variant="muted"
        className="text-xs flex-1 mb-3"
      >
        Enter your number and the agent will call you right away.
      </Text>
      {state === "ok" ? (
        <View
          className="flex items-center gap-2 rounded-lg px-3 py-2.5 text-sm"
          style={{ backgroundColor: `${accent}14`, color: accent }}
        >
          <Icon
            name="check-circle"
            size={16}
          />
          Calling you now — answer your phone!
        </View>
      ) : (
        <>
          <View className="flex items-center gap-2">
            <input
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="+1 555 123 4567"
              onKeyDown={(e) => {
                if (e.key === "Enter") dial();
              }}
              className="flex-1 min-w-0 rounded-lg border border-gray-200 dark:border-white/10 bg-white dark:bg-white/[0.04] px-3 py-2 text-sm text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2"
              style={{ ["--tw-ring-color" as any]: accent }}
            />
            <button
              onClick={dial}
              disabled={!phone.trim() || dialing}
              className="shrink-0 rounded-lg px-4 py-2 text-sm font-semibold text-white transition-all hover:brightness-110 disabled:opacity-50 flex items-center justify-center gap-1.5"
              style={{ backgroundColor: accent }}
            >
              {dialing ? (
                <Icon
                  name="loader"
                  size={15}
                  className="animate-spin"
                />
              ) : (
                <Icon
                  name="phone-outbound"
                  size={15}
                />
              )}
              {dialing ? "Calling…" : "Call me"}
            </button>
          </View>
          {state === "error" && <Text className="text-xs text-red-600 dark:text-red-400 mt-2">{error}</Text>}
        </>
      )}
    </View>
  );
};

// ---------------------------------------------------------------------------

const Centered = ({ icon, title, subtitle }: { icon: string; title: string; subtitle: string }) => (
  <View className="h-screen overflow-y-auto flex items-center justify-center p-4 bg-[#fafaf9] dark:bg-[#0a0b0d]">
    <View className="text-center space-y-3 max-w-sm">
      <View className="h-11 w-11 rounded-xl bg-gray-100 dark:bg-white/[0.04] border border-gray-200 dark:border-white/10 flex items-center justify-center mx-auto">
        <Icon
          name={icon}
          size={18}
          className="text-gray-400"
        />
      </View>
      <Text className="text-lg font-bold text-gray-900 dark:text-white block">{title}</Text>
      <Text
        variant="muted"
        className="text-sm block"
      >
        {subtitle}
      </Text>
      <Text className="text-[10px] uppercase tracking-[0.2em] text-gray-400 dark:text-gray-600 pt-4 block">Powered by VoiceRun</Text>
    </View>
  </View>
);
